import { Record, User } from './general'

// ..\mowazaf\Data\Employee\Entities\UserEducation.cs
export interface UserEducation extends Record {
    id: number;
    userId: string;
    user?: User;
    institutionName: string;
    degree: string;
    fieldOfStudy?: string;
    startDate?: Date;
    endDate?: Date;
    grade?: string;
}

// ..\mowazaf\Data\Employee\Entities\UserWorkExperience.cs
export interface UserWorkExperience extends Record {
    id: number;
    userId: string;
    user?: User;
    companyName: string;
    jobTitle: string;
    jobDescription?: string;
    startDate?: Date;
    endDate?: Date;
    isCurrentJob: boolean;
    location?: string;
    managerName?: string;
    managerContact?: string;
}

// ..\mowazaf\Data\Employee\InputModels\UserEducationInput.cs
export interface UserEducationInput {
    id: number;
    userId: string;
    institutionName?: string;
    degree?: string;
    fieldOfStudy?: string;
    startDate?: Date;
    endDate?: Date;
    grade?: string;
}

// ..\mowazaf\Data\Employee\InputModels\UserWorkExperienceInput.cs
export interface UserWorkExperienceInput {
    id: number;
    userId: string;
    companyName?: string;
    jobTitle?: string;
    jobDescription?: string;
    startDate?: Date;
    endDate?: Date;
    isCurrentJob: boolean;
    location?: string;
    managerName?: string;
    managerContact?: string;
}

// ..\mowazaf\Data\Employee\ViewModels\UserEducationView.cs
export interface UserEducationView {
    id: number;
    userId: string;
    user?: User;
    institutionName: string;
    degree: string;
    fieldOfStudy?: string;
    startDate?: Date;
    endDate?: Date;
    grade?: string;
}

// ..\mowazaf\Data\Employee\ViewModels\UserWorkExperienceView.cs
export interface UserWorkExperienceView {
    id: number;
    userId: string;
    user?: User;
    companyName: string;
    jobTitle: string;
    jobDescription?: string;
    startDate?: Date;
    endDate?: Date;
    isCurrentJob: boolean;
    location?: string;
    managerName?: string;
    managerContact?: string;
}

// ..\mowazaf\Data\Employee\Entities\ProficiencyLevel.cs
export enum ProficiencyLevel {
    Awareness = 'Awareness',
    Working = 'Working',
    Strong = 'Strong'
}

// ..\mowazaf\Data\Employee\ViewModels\UserSkillView.cs
export interface UserSkillView {
    id: number;
    userId: string;
    skillId: number;
    skillName: string;
    proficiency: ProficiencyLevel;
    reportedById?: string;
    reportedByName?: string;
    createdOn: Date;
    /**
     * CO-448 §3.4.4 — the server view model has carried `EndorsementCount` since it was written,
     * but NO projection populated it and this interface never declared it, so the count was
     * invisible on every client surface. The endorse button reads it; without the field the
     * button renders "0" forever and looks dead. Read it as `endorsementCount ?? 0` at every
     * site: an older server build (or a projection W3-D has not fixed yet) simply omits the key.
     */
    endorsementCount: number;
    /**
     * TRUE when the CURRENT viewer has already endorsed this skill. Named to match the server
     * property `UserSkillView.HasEndorsedByCurrentUser` — spec §3.3.1/§3.4.4 never mentioned this
     * field at all; W3-D added it because without it the ThumbsUp renders enabled after a reload
     * and the second click fails server-side, which reads as a broken button.
     *
     * Still optional here: an older server build simply omits the key, and in that case the
     * profile page falls back to a session-local set of ids endorsed during this visit. The server
     * refuses the duplicate either way, so the worst case is a rejected request, never a double count.
     */
    hasEndorsedByCurrentUser?: boolean;
}

// ..\mowazaf\Data\Employee\InputModels\UserSkillInput.cs
export interface UserSkillInput {
    id: number;
    userId: string;
    skillName: string;
    proficiency: ProficiencyLevel;
}

// ..\mowazaf\Data\Employee\ViewModels\SkillView.cs
export interface SkillView {
    id: number;
    name: string;
    description?: string;
}

// ..\mowazaf\Data\Employee\InputModels\SkillInput.cs
export interface SkillInput {
    name: string;
    description?: string;
}

// ..\mowazaf\Data\Employee\InputModels\SkillFilterInput.cs
export interface SkillFilterInput {
    searchTerm?: string;
    departmentId?: number;
    proficiency?: ProficiencyLevel;
}

// Proficiency breakdown for inventory view. camelCase, like every other view model here: the
// serializer camel-cases the whole graph, not just the root object, so the C# ProficiencyBreakdown
// arrives as {"strong":n,"working":n,"awareness":n}. Reading .Strong/.Working/.Awareness returned
// undefined, which is why the exported CSV had undefined in those three columns and the inventory
// bars had no width.
export interface ProficiencyBreakdown {
    strong: number;
    working: number;
    awareness: number;
}

// ..\mowazaf\Data\Employee\ViewModels\SkillInventoryView.cs
export interface SkillInventoryView {
    skillId: number;
    skillName: string;
    totalEmployees: number;
    byProficiency: ProficiencyBreakdown;
    departments: string[];
}

// ..\mowazaf\Data\Employee\Entities\SkillEvidenceType.cs
export enum SkillEvidenceType {
    SkillAdded = 'SkillAdded',
    Endorsement = 'Endorsement',
    ProficiencyChange = 'ProficiencyChange',
    Feedback = 'Feedback'
}

// ..\mowazaf\Data\Employee\ViewModels\SkillEvidenceView.cs
export interface SkillEvidenceView {
    id: number;
    userSkillId: number;
    evidenceType: SkillEvidenceType;
    title: string;
    description?: string;
    authorId?: string;
    authorName?: string;
    evidenceDate: Date;
}

/* ──────────────────────────────────────────────────────────────────────────────────────────────
   CO-446 / CO-447 — DERIVED STRENGTHS. THE SINGLE DEFINITION.

   🔴 These three interfaces are the ONLY client definition of a strength. Before CO-447 there
   were FIVE hand-rolled copies of two shapes:
     `Strength`        in views/UserProfile/Skills/index.tsx, components/StrengthsSection.tsx
                       and components/SkillStrengthDetailDrawer.tsx (byte-identical);
     `StrengthCluster` in views/EmployeesCycle/SkillsOverview/index.tsx and
                       components/StrengthClustersSection.tsx.
   None of them matched the server. They only agreed with each other because nothing ever
   populated them — every one of those arrays was a literal `[]` with no setter. Declaring a
   sixth copy anywhere (an api module included) is how the duplicates came back the first time.
   `SurfedStrengthsSkillsPanel` IMPORTS from here; it does not re-declare.

   Mirrors `Mowazaf.Server/Data/Employee/ViewModels/UserStrengthView.cs` (spec §3.3.1).
   ────────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * `Mowazaf.Server/Data/Employee/Entities/UserStrength.cs` — `StrengthConfidence { Low, Medium,
 * High }`. A STRING on the wire: `Program.cs` registers a global `StringEnumConverter`, so every
 * enum serializes by NAME (§1.3). Declared as a union rather than a TS `enum` so a value the
 * server adds later cannot silently become `undefined` at a comparison site.
 */
export type StrengthConfidence = 'Low' | 'Medium' | 'High';

const STRENGTH_CONFIDENCE_BY_ORDINAL: { [k: number]: StrengthConfidence } = {
    0: 'Low',
    1: 'Medium',
    2: 'High',
};

/**
 * Coerces whatever the wire carried into the string confidence. Same defensive shape as
 * `normalizeSnapshotStatus` (api/PreformanceManagement/PerformanceSnapshot.ts:31), and for the
 * same reason: CO-429 shipped a page that compared a string enum to a number, every comparison
 * was permanently false, and the action bar never rendered on any snapshot.
 *
 * Unknown input reads as `'Low'` — the value that claims the LEAST. Defaulting an unrecognised
 * confidence to `'High'` would put an unearned badge on a strength nobody corroborated.
 */
export function normalizeStrengthConfidence(raw: unknown): StrengthConfidence {
    if (typeof raw === 'string') {
        const match = (Object.values(STRENGTH_CONFIDENCE_BY_ORDINAL) as string[]).find(
            (c) => c.toLowerCase() === raw.toLowerCase(),
        );
        if (match) return match as StrengthConfidence;
        const parsed = Number(raw);
        if (!Number.isNaN(parsed) && STRENGTH_CONFIDENCE_BY_ORDINAL[parsed]) {
            return STRENGTH_CONFIDENCE_BY_ORDINAL[parsed];
        }
        return 'Low';
    }
    if (typeof raw === 'number' && STRENGTH_CONFIDENCE_BY_ORDINAL[raw]) {
        return STRENGTH_CONFIDENCE_BY_ORDINAL[raw];
    }
    return 'Low';
}

/**
 * `Mowazaf.Server/Data/Employee/Entities/SkillEvidence.cs` — `SkillEvidenceSourceType`. Where a
 * piece of evidence for a strength came from. String on the wire, same as above.
 */
export type SkillEvidenceSourceType =
    | 'Unknown'
    | 'Recognition'
    | 'Goal'
    | 'GoalUpdate'
    | 'CheckIn'
    | 'TeamHubPost'
    | 'TeamHubPostComment'
    | 'ManagerReflectionResponse'
    | 'ManualEndorsement';

const SKILL_EVIDENCE_SOURCE_BY_ORDINAL: { [k: number]: SkillEvidenceSourceType } = {
    0: 'Unknown',
    1: 'Recognition',
    2: 'Goal',
    3: 'GoalUpdate',
    4: 'CheckIn',
    5: 'TeamHubPost',
    6: 'TeamHubPostComment',
    7: 'ManagerReflectionResponse',
    8: 'ManualEndorsement',
};

/** Unknown input reads as `'Unknown'`, which the UI renders with a neutral icon and a generic label. */
export function normalizeSkillEvidenceSourceType(raw: unknown): SkillEvidenceSourceType {
    if (typeof raw === 'string') {
        const match = (Object.values(SKILL_EVIDENCE_SOURCE_BY_ORDINAL) as string[]).find(
            (s) => s.toLowerCase() === raw.toLowerCase(),
        );
        if (match) return match as SkillEvidenceSourceType;
        const parsed = Number(raw);
        if (!Number.isNaN(parsed) && SKILL_EVIDENCE_SOURCE_BY_ORDINAL[parsed]) {
            return SKILL_EVIDENCE_SOURCE_BY_ORDINAL[parsed];
        }
        return 'Unknown';
    }
    if (typeof raw === 'number' && SKILL_EVIDENCE_SOURCE_BY_ORDINAL[raw]) {
        return SKILL_EVIDENCE_SOURCE_BY_ORDINAL[raw];
    }
    return 'Unknown';
}

/**
 * ..\mowazaf\Data\Employee\ViewModels\UserStrengthView.cs
 *
 * Dates are typed `string` (the raw ISO-8601 the wire carries), NOT `Date`. The older view models
 * in this file say `Date` and are lying — `JSON.parse` never produces a `Date`, which is why every
 * call site wraps them in `new Date(...)` anyway. New models tell the truth; parse at the render
 * site with `new Date(...)` or `parseISO(...)`.
 */
export interface UserStrengthView {
    id: number;
    /** NULL for an AI-only strength that matched no catalogue skill — see `isAiOnly`. */
    skillId?: number | null;
    label: string;
    confidence: StrengthConfidence;
    /** How many DISTINCT people evidenced it. This, not raw volume, is what confidence scales on. */
    distinctAuthorCount: number;
    evidenceCount: number;
    firstObservedAt: string;
    lastConfirmedAt: string;
    /** Proposed by the AI refinement pass and matched to no catalogue skill. Label it as such in the UI. */
    isAiOnly: boolean;
    /** Server-side `SuppressedAt != null`. A suppressed strength is hidden from the default list. */
    isSuppressed: boolean;
}

/**
 * ..\mowazaf\Data\Employee\ViewModels\UserStrengthEvidenceView.cs
 *
 * 🔴 `quotedSpan` is a verbatim excerpt of PRIVATE text (check-in manager notes, reflections).
 * The endpoint that serves it is restricted to the subject, the subject's manager and HR/Admin.
 * Never render it on a surface a wider audience can reach, and never log it.
 */
export interface UserStrengthEvidenceView {
    id: number;
    sourceType: SkillEvidenceSourceType;
    sourceId: string;
    /** Resolved display name. The raw author id is deliberately not on the wire. */
    authorName?: string;
    occurredAt: string;
    quotedSpan?: string;
}

/** ..\mowazaf\Data\Employee\ViewModels\StrengthClusterView.cs — one recurring strength across a company. */
export interface StrengthClusterView {
    label: string;
    /** DISTINCT employees holding it. The old client shape called this `frequency`, which it never was. */
    peopleCount: number;
    departments: string[];
    /** Modal confidence across the cluster, not an average. */
    confidence: StrengthConfidence;
    /** First observed within the last 90 days. */
    emergingTrend: boolean;
}

/**
 * The `GetCompanyStrengthClusters` payload. Spec §3.3.1 types the endpoint as a bare
 * `List<StrengthClusterView>` while §3.5.4 asks the same endpoint to carry a sibling suppression
 * summary — the two are not reconcilable, and W3-D owns which one ships. The api module therefore
 * NORMALISES both shapes into this one (see api/Employee/skills.ts), so this client is correct
 * either way and cannot break on the day the summary is added.
 */
export interface StrengthClustersResultView {
    clusters: StrengthClusterView[];
    totalStrengths: number;
    suppressedStrengths: number;
    /**
     * PERCENT, 0-100 (the server's `CompanyStrengthClustersView.SuppressionRate` convention —
     * already multiplied by 100, do NOT multiply again). Read, not enforced: above roughly 30 the
     * detector is over-firing and its whole-word matcher needs tightening.
     */
    suppressionRate: number;
    /** FALSE when the server sent a bare array, so the UI hides the rate instead of printing a fake 0. */
    hasSuppressionSummary: boolean;
}

// ..\mowazaf\Data\Employee\Entities\SocialLinkPlatform.cs
export enum SocialLinkPlatform {
    LinkedIn = 'LinkedIn',
    GitHub = 'GitHub',
    Twitter = 'Twitter',
    Facebook = 'Facebook',
    Instagram = 'Instagram',
    YouTube = 'YouTube',
    Website = 'Website',
    Other = 'Other'
}

// ..\mowazaf\Data\Employee\Entities\UserSocialLink.cs
export interface UserSocialLink extends Record {
    id: number;
    userId: string;
    user?: User;
    platform: SocialLinkPlatform;
    url: string;
    displayName?: string;
}

// ..\mowazaf\Data\Employee\InputModels\UserSocialLinkInput.cs
export interface UserSocialLinkInput {
    id: number;
    userId: string;
    platform: SocialLinkPlatform;
    url: string;
    displayName?: string;
}

// ..\mowazaf\Data\Employee\ViewModels\UserSocialLinkView.cs
export interface UserSocialLinkView {
    id: number;
    userId: string;
    platform: SocialLinkPlatform;
    url: string;
    displayName?: string;
}

// ..\mowazaf\Data\Employee\Entities\AchievementCategory.cs
export enum AchievementCategory {
    Recognition = 'Recognition',
    Collaboration = 'Collaboration',
    Innovation = 'Innovation',
    Excellence = 'Excellence',
    Milestone = 'Milestone',
    Leadership = 'Leadership',
    Other = 'Other'
}

// ..\mowazaf\Data\Employee\Entities\ProfileAchievement.cs
export interface ProfileAchievement {
    id: number;
    userId: string;
    title: string;
    description?: string;
    icon: string;
    category: AchievementCategory;
    earnedDate: Date;
    isPublic: boolean;
}

// ..\mowazaf\Data\Employee\InputModels\ProfileAchievementInput.cs
export interface ProfileAchievementInput {
    id: number;
    userId: string;
    title: string;
    description?: string;
    icon: string;
    category: AchievementCategory;
    earnedDate?: Date;
    isPublic: boolean;
}

// ..\mowazaf\Data\Employee\ViewModels\ProfileAchievementView.cs
export interface ProfileAchievementView {
    id: number;
    userId: string;
    title: string;
    description?: string;
    icon: string;
    category: AchievementCategory;
    earnedDate: Date;
    isPublic: boolean;
}

// ── Employment Lifecycle Settings ───────────────────────────────

// ..\mowazaf\Data\Employee\ViewModels\MovementTypeView.cs
export interface MovementTypeView {
    id: number;
    name: string;
    isActive: boolean;
    companyId: number;
}

// ..\mowazaf\Data\Employee\InputModels\MovementTypeInput.cs
export interface MovementTypeInput {
    id?: number;
    name: string;
    isActive: boolean;
}

// ..\mowazaf\Data\Employee\ViewModels\ExitReasonView.cs
export interface ExitReasonView {
    id: number;
    name: string;
    isActive: boolean;
    // Voluntary resignation (true) vs involuntary termination (false). Null/undefined = unclassified (name-heuristic fallback).
    isResignation?: boolean | null;
    // CO-670 — the end-of-service category driving the GRATUITY (isResignation only decides the status
    // label). PascalCase DepartureReason name, e.g. "TerminationWithoutCause"; null/undefined = unmapped,
    // which falls back to the classification above.
    // Typed `string`, NOT DepartureReasonEnum, deliberately: importing that type from ./payroll here
    // reorders module resolution enough to re-print an unrelated union in a DIFFERENT member order,
    // which breaks three baselined BillingIndex TS2367 keys that match on the error text. The value is
    // constrained where it is actually produced and consumed — the picker only offers the 14 options
    // (models/departureReasons.ts) and the server rejects anything that is not a defined enum member
    // (ExitReason tool schema `enum` + ToolParams.GetEnum's Enum.IsDefined check).
    companyId: number;
}

// ..\mowazaf\Data\Employee\InputModels\ExitReasonInput.cs
export interface ExitReasonInput {
    id?: number;
    name: string;
    isActive: boolean;
    isResignation?: boolean | null;
    /** See ExitReasonView.eosCategory. Send it on EVERY update: the server's update is a full replace. */
    eosCategory?: string | null;
}

// ..\mowazaf\Data\Employee\ViewModels\OnboardingTaskTypeView.cs
export interface OnboardingTaskTypeView {
    id: number;
    name: string;
    isActive: boolean;
    companyId: number;
}

// ..\mowazaf\Data\Employee\InputModels\OnboardingTaskTypeInput.cs
export interface OnboardingTaskTypeInput {
    id?: number;
    name: string;
    isActive: boolean;
}

// ..\mowazaf\Data\Employee\ViewModels\OffboardingTaskTypeView.cs
export interface OffboardingTaskTypeView {
    id: number;
    name: string;
    isActive: boolean;
    companyId: number;
}

// ..\mowazaf\Data\Employee\InputModels\OffboardingTaskTypeInput.cs
export interface OffboardingTaskTypeInput {
    id?: number;
    name: string;
    isActive: boolean;
}

// ..\mowazaf\Data\Employee\ViewModels\MovementTaskTypeView.cs
export interface MovementTaskTypeView {
    id: number;
    name: string;
    isActive: boolean;
    companyId: number;
}

// ..\mowazaf\Data\Employee\InputModels\MovementTaskTypeInput.cs
export interface MovementTaskTypeInput {
    id?: number;
    name: string;
    isActive: boolean;
}

// ── Employment Lifecycle Templates ──────────────────────────────

export enum TemplateCategory {
    Onboarding = 0,
    Offboarding = 1,
    Movement = 2,
}

export interface LifecycleTemplateItemView {
    id: number;
    templateId: number;
    name: string;
    typeId?: number;
    typeCategory?: string;
    typeName?: string;
    mandatory: boolean;
    isBlocking: boolean;
    isPolicyAcceptanceTask: boolean;
    defaultOwner: string;
    dueDateOffset: number;
    sortOrder: number;
}

export interface LifecycleTemplateView {
    id: number;
    name: string;
    description?: string;
    isDefault: boolean;
    templateCategory: TemplateCategory;
    companyId: number;
    items: LifecycleTemplateItemView[];
}

export interface LifecycleTemplateItemInput {
    name: string;
    typeId?: number;
    mandatory: boolean;
    isBlocking: boolean;
    isPolicyAcceptanceTask: boolean;
    defaultOwner: string;
    dueDateOffset: number;
    sortOrder: number;
}

export interface LifecycleTemplateInput {
    id?: number;
    name: string;
    description?: string;
    isDefault: boolean;
    templateCategory: TemplateCategory;
    items: LifecycleTemplateItemInput[];
}

export interface DuplicateTemplateInput {
    newName: string;
}

// ── Employment Lifecycle - Onboarding Processes ─────────────────

export enum OnboardingStatus {
    NotStarted = 'NotStarted',
    InProgress = 'InProgress',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
}

export enum ProcessTaskStatus {
    Pending = 'Pending',
    InProgress = 'InProgress',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
}

export enum EmploymentStatus {
    Active = 'Active',
    OnProbation = 'OnProbation',
    Onboarding = 'Onboarding',
    Offboarding = 'Offboarding',
    Terminated = 'Terminated',
    Resigned = 'Resigned',
}

export interface OnboardingProcessTaskView {
    id: number;
    processId: number;
    name: string;
    typeId?: number;
    typeName?: string;
    status: ProcessTaskStatus;
    dueDate?: string;
    mandatory: boolean;
    isBlocking: boolean;
    isPolicyAcceptanceTask: boolean;
    assigneeId?: string;
    assigneeName?: string;
    sortOrder: number;
}

export interface OnboardingProcessView {
    id: number;
    userId: string;
    employeeName?: string;
    staffNumber?: string;
    templateId?: number;
    templateName?: string;
    startDate: string;
    managerId: string;
    managerName?: string;
    position: string;
    department: string;
    status: OnboardingStatus;
    completionPercentage: number;
    companyId: number;
    tasks: OnboardingProcessTaskView[];
}

export interface OnboardingProcessInput {
    userId: string;
    templateId?: number;
    startDate: string;
    managerId: string;
}

export interface OnboardingProcessTaskInput {
    name: string;
    typeId?: number;
    mandatory: boolean;
    isBlocking: boolean;
    isPolicyAcceptanceTask: boolean;
    dueDate?: string;
    assigneeId?: string;
    sortOrder: number;
}

export interface MyBlockingStatusView {
    isBlocked: boolean;
    termsPending: boolean;
    currentTermsVersion?: number;
    onboardingProcessId?: number;
    blockingTasks: OnboardingProcessTaskView[];
    nonBlockingTasks: OnboardingProcessTaskView[];
}

export interface DashboardOnboardingTaskView {
    id: number;
    processId: number;
    name: string;
    status: ProcessTaskStatus;
    dueDate?: string;
    mandatory: boolean;
    isBlocking: boolean;
    isPolicyAcceptanceTask: boolean;
    assigneeId?: string;
    assigneeName?: string;
    employeeId: string;
    employeeName?: string;
    /** "Onboarding" | "Offboarding" | "Movement" */
    lifecycleType: string;
}

export interface UpdateTaskStatusInput {
    status: ProcessTaskStatus;
}

export interface EmployeeSelectionView {
    id: string;
    fullName: string;
    title?: string;
    department?: string;
    staffNumber?: string;
}

// ── Employment Lifecycle - Offboarding Processes ─────────────────

export enum OffboardingStatus {
    NotStarted = 'NotStarted',
    InProgress = 'InProgress',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
}

export interface OffboardingProcessTaskView {
    id: number;
    processId: number;
    name: string;
    typeId?: number;
    typeName?: string;
    status: ProcessTaskStatus;
    dueDate?: string;
    mandatory: boolean;
    isBlocking: boolean;
    assigneeId?: string;
    assigneeName?: string;
    sortOrder: number;
}

export interface OffboardingProcessView {
    id: number;
    userId: string;
    employeeName?: string;
    staffNumber?: string;
    templateId?: number;
    templateName?: string;
    lastWorkingDay: string;
    exitReasonId: number;
    exitReasonName?: string;
    managerId: string;
    managerName?: string;
    position: string;
    department: string;
    status: OffboardingStatus;
    completionPercentage: number;
    eosAmount?: number;
    currencyCode?: string;
    companyId: number;
    tasks: OffboardingProcessTaskView[];
}

export interface OffboardingProcessInput {
    userId: string;
    templateId?: number;
    lastWorkingDay: string;
    exitReasonId: number;
    managerId: string;
    /** CO-657 — required by the server for ANY past last working day (owner decision 2026-09-14). */
    backDateReason?: string;
}

// Fast-terminate (no offboarding checklist) — creates no OffboardingProcess row.
export interface TerminateDirectInput {
    userId: string;
    exitReasonId?: number;
    lastWorkingDay?: string;
    rehireEligible?: boolean;
    /** CO-657 — required by the server for ANY past last working day (owner decision 2026-09-14). */
    backDateReason?: string;
}

/**
 * CO-657 — what recording a PAST exit date did to work dated after it.
 * Mirrors `Mowazaf.Server/Data/Employee/ViewModels/PostExitSummaryView.cs`.
 *
 * Only three of these describe a change the system made — `shiftsArchived`, `leaveRejected`,
 * `shiftPayRejected`. Everything else is FLAG ONLY: counted for HR, never reversed (owner
 * decision 2026-09-14, spec §10 Q4/Q5). The UI must not imply otherwise.
 */
export interface PostExitSummaryView {
    backDated: boolean;
    daysBackDated: number;
    exitDate: string;
    shiftsArchived: number;
    attendanceSessions: number;
    leaveRejected: number;
    leaveApprovedAfterExit: number;
    leaveOverlapping: number;
    accrualCreditsAfterExit: number;
    payslipsAfterExit: number;
    shiftPayRejected: number;
    shiftPayFlagged: number;
    warnings: string[];
    hasFindings: boolean;
}

export interface TerminateDirectResultView {
    userId: string;
    employmentStatus: string;
    lastWorkingDay?: string;
    terminationDate?: string;
    eosCalculated: boolean;
    eosAmount?: number;
    note?: string;
    /** Null when the exit date is today or in the future — nothing can be after it yet. */
    postExit?: PostExitSummaryView | null;
}

// Reverse a completed termination/resignation (A4). Reason is mandatory + audit-logged; a Paid
// settlement is only reversed when allowPaidSettlementReversal is set.
export interface ReverseTerminationInput {
    userId: string;
    reason: string;
    allowPaidSettlementReversal?: boolean;
}

export interface ReverseTerminationResultView {
    userId: string;
    employmentStatus: string;
    rehireEligible: boolean;
    shape: string;
    endOfServiceArchived: boolean;
    settlementArchived: boolean;
    paidSettlementReversed: boolean;
    note?: string;
}

export interface OffboardingProcessTaskInput {
    name: string;
    typeId?: number;
    mandatory: boolean;
    isBlocking: boolean;
    dueDate?: string;
    assigneeId?: string;
    sortOrder: number;
}

// ── Employment Lifecycle - Internal Movements ─────────────────

export enum ApprovalStatus {
    PendingManagerApproval = 'PendingManagerApproval',
    PendingHrApproval = 'PendingHrApproval',
    Approved = 'Approved',
    Rejected = 'Rejected',
    /**
     * The manager GAINING the employee must accept them before the movement reaches HR.
     * Server-side this is ApprovalStatus.PendingReceivingManagerApproval, APPENDED as value 4
     * (see Data/Employee/Entities/ApprovalStatus.cs) and serialised by name.
     *
     * Listed last to mirror the server's numbering, NOT the chain order — the chain runs
     * PendingManagerApproval -> PendingReceivingManagerApproval -> PendingHrApproval.
     * Omitting this member left every movement parked at the receiving stage rendering no
     * waiting-on chip at all, i.e. a stalled approval invisible until someone complained.
     */
    PendingReceivingManagerApproval = 'PendingReceivingManagerApproval',
}

export enum MovementStatus {
    NotStarted = 'NotStarted',
    InProgress = 'InProgress',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
}

export interface MovementTaskView {
    id: number;
    movementId: number;
    name: string;
    typeId?: number;
    typeName?: string;
    status: ProcessTaskStatus;
    dueDate?: string;
    mandatory: boolean;
    isBlocking: boolean;
    assigneeId?: string;
    assigneeName?: string;
    sortOrder: number;
}

export interface InternalMovementView {
    id: number;
    userId: string;
    employeeName?: string;
    staffNumber?: string;
    movementTypeId: number;
    movementTypeName?: string;
    templateId?: number;
    templateName?: string;
    currentPosition: string;
    newPosition: string;
    currentDepartmentId?: number;
    currentDepartmentName?: string;
    newDepartmentId?: number;
    newDepartmentName?: string;
    effectiveDate: string;
    reason?: string;
    approvalStatus: ApprovalStatus;
    managerApprovedById?: string;
    managerApprovedByName?: string;
    managerApprovalDate?: string;
    hrApprovedById?: string;
    hrApprovedByName?: string;
    hrApprovalDate?: string;
    rejectedById?: string;
    rejectedByName?: string;
    rejectionReason?: string;
    status: MovementStatus;
    completionPercentage: number;
    companyId: number;
    tasks: MovementTaskView[];
}

export interface InternalMovementInput {
    userId: string;
    movementTypeId: number;
    templateId?: number;
    newPosition: string;
    newDepartmentId?: number;
    effectiveDate: string;
    reason?: string;
    /**
     * RECEIVING manager: the manager gaining the employee, i.e. the approver of the
     * PendingReceivingManagerApproval stage. Optional by contract on both sides.
     *
     * Omitting it is a real product decision, not a default: the server's
     * ResolveReceivingManagerIdAsync returns null and NextStageAfterReleaseAsync deliberately
     * skips the receiving stage, sending the movement straight to HR. The initiate form
     * therefore states that consequence on screen instead of leaving the collapse silent.
     */
    managerId?: string;
}

export interface InternalMovementUpdateInput {
    movementTypeId: number;
    newPosition: string;
    newDepartmentId?: number;
    effectiveDate: string;
    reason?: string;
}

export interface MovementTaskInput {
    name: string;
    typeId?: number;
    mandatory: boolean;
    isBlocking: boolean;
    dueDate?: string;
    assigneeId?: string;
    sortOrder: number;
}

export interface RejectMovementInput {
    rejectionReason: string;
}

export interface EmployeeMovementHistoryView {
    id: number;
    userId: string;
    employeeName?: string;
    movementId: number;
    changeType: string;
    fieldName: string;
    oldValue?: string;
    newValue?: string;
    effectiveDate: string;
}

export interface DepartmentSelectionView {
    id: number;
    name: string;
}

// ..\mowazaf\Data\Employee\Entities\EmployeeMovement.cs
export enum MovementType {
    Promotion = 'Promotion',
    Transfer = 'Transfer',
    Demotion = 'Demotion',
    LateralMove = 'LateralMove',
    SalaryAdjustment = 'SalaryAdjustment',
    ContractRenewal = 'ContractRenewal'
}

export enum MovementApprovalStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Rejected = 'Rejected',
    Cancelled = 'Cancelled'
}

export interface EmployeeMovementInput {
    userId: string;
    movementType: MovementType;
    newTitle?: string;
    newDepartmentId?: number;
    newBranchId?: number;
    newSalary?: number;
    effectiveDate: string;
    reason?: string;
    notes?: string;
}

export interface EmployeeMovementView extends Record {
    id: number;
    userId: string;
    userName?: string;
    companyId: number;
    movementType: MovementType;
    previousTitle?: string;
    previousDepartmentId?: number;
    previousDepartmentName?: string;
    previousBranchId?: number;
    previousBranchName?: string;
    previousSalary?: number;
    newTitle?: string;
    newDepartmentId?: number;
    newDepartmentName?: string;
    newBranchId?: number;
    newBranchName?: string;
    newSalary?: number;
    effectiveDate: string;
    reason?: string;
    approvalStatus: MovementApprovalStatus;
    approvedById?: string;
    approvedByName?: string;
    approvedOn?: string;
    notes?: string;
}
